const HTTP_STATUS_INVALID = 422;
const HTTP_STATUS_SERVER_ERROR = 500;

class ResponseData {
    constructor(data, status, statusText, headers) {
        this._data = data;
        this._status = status;
        this._statusText = statusText;
        this._headers = headers;
    }

    /**
     * @returns {Object|Array|string}
     */
    get data() {
        return this._data;
    }

    get isJson() {
        return this._headers.get('Content-Type') === 'application/json';
    }

    get status() {
        return this._status;
    }

    get statusText() {
        return this._statusText;
    }

    get headers() {
        return this._headers;
    }
}

class ResponseError extends Error {
    /**
     * @param response
     * @param {ResponseData} responseData
     */
    constructor(response, responseData) {
        super(responseData.isJson ? responseData.data?.message || response.statusText : response.statusText);
        this._response = response;
        this._responseData = responseData;
    }

    /**
     * @returns {ResponseData}
     */
    get response() {
        return this._responseData;
    }

    get isValidationError() {
        return this._response.status === HTTP_STATUS_INVALID;
    }

    get isServerError() {
        return this._response.status >= HTTP_STATUS_SERVER_ERROR;
    }
}

class Http {
    /**
     * @param {RequestInfo|URL} url
     * @param {RequestInit} options
     * @returns {Promise<ResponseData>}
     */
    static call(url, options = {}) {
        return this._request(url, options);
    }

    static async _request(url, options = {}) {
        const response = await fetch(url, {
            method: 'GET',
            ...options,
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                ...options.headers,
            },
        });

        let data = null;

        if ((response.headers.get('Content-Type') || '').includes('application/json')) {
            data = await response.json();
        } else {
            data = await response.text();
        }

        const responseData = new ResponseData(data, response.status, response.statusText, response.headers);

        if (!response.ok) {
            throw new ResponseError(response, responseData);
        }

        return responseData;
    }
}

export {
    ResponseData,
    ResponseError,
    Http,
};
